import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { requiredFieldArrayCheckbox } from '../../util/validators';
import { TERMS_WORDING_BUSINESS } from '../../util/termsAcceptance';

import ExternalLink from '../ExternalLink/ExternalLink';
import FieldCheckbox from '../FieldCheckbox/FieldCheckbox';

import css from './FieldTermsAcceptance.module.css';

// The terms and the privacy policy open in a new tab, so a half-filled
// sign-up form is still there when the reader comes back.
const TERMS_PATH = '/brukervilkar';
const PRIVACY_PATH = '/personvern';

/**
 * The required "I accept the terms" checkbox, and the line about the privacy
 * policy directly under it. Used on the sign-up form, on the Vipps / Google /
 * Facebook confirm step, and in the prompt that asks existing members once.
 *
 * The wording depends on who is signing up: a private person confirms they
 * are over 18 and use Zuprize as a private person; someone creating a
 * business account from an invite confirms they may act for the business.
 * The form cannot be submitted until the box is ticked.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id - input id, unique on the page
 * @param {'private'|'business'} [props.wording]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const FieldTermsAcceptance = props => {
  const { id, wording = 'private', className } = props;
  const intl = useIntl();
  const isBusiness = wording === TERMS_WORDING_BUSINESS;

  const termsLink = (
    <ExternalLink href={TERMS_PATH} className={css.link}>
      <FormattedMessage id="FieldTermsAcceptance.termsLinkText" />
    </ExternalLink>
  );
  const privacyLink = (
    <ExternalLink href={PRIVACY_PATH} className={css.link}>
      <FormattedMessage id="FieldTermsAcceptance.privacyLinkText" />
    </ExternalLink>
  );

  return (
    <div className={classNames(css.root, className)}>
      <FieldCheckbox
        id={id}
        name="termsAccepted"
        value="yes"
        label={
          <FormattedMessage
            id={
              isBusiness
                ? 'FieldTermsAcceptance.businessLabel'
                : 'FieldTermsAcceptance.privateLabel'
            }
            values={{ termsLink }}
          />
        }
        validate={requiredFieldArrayCheckbox(
          intl.formatMessage({ id: 'FieldTermsAcceptance.required' })
        )}
        aria-required="true"
      />
      <p className={css.privacyNotice}>
        <FormattedMessage id="FieldTermsAcceptance.privacyNotice" values={{ privacyLink }} />
      </p>
    </div>
  );
};

export default FieldTermsAcceptance;
